if (!customElements.get('product-gallery-zoom')) {
  customElements.define(
    'product-gallery-zoom',
    class ProductGalleryZoom extends HTMLElement {
      connectedCallback() {
        this.slidesWrapper = this.querySelector('[data-zoom-slides]');
        this.thumbsWrapper = this.querySelector('[data-zoom-thumbs]');
        this.slider = this.querySelector('.zoom-slider');
        this.thumbsSlider = this.querySelector('.zoom-thumbs');
        this.closeButton = this.querySelector('[data-close-zoom]');
        this.triggerEl = null;
        this.inertedElements = [];

        this.openHandler = (event) => this.open(event.detail);
        document.addEventListener('gallery:open-zoom', this.openHandler);

        this.keydownHandler = (event) => this.handleKeydown(event);

        if (this.closeButton) this.closeButton.addEventListener('click', () => this.close());
        this.addEventListener('click', (event) => {
          if (event.target === this) this.close();
        });
      }

      get dragEnabled() {
        return this.dataset.dragEnabled === 'true';
      }

      get isMobile() {
        return window.innerWidth < 1024;
      }

      get useSwiperZoom() {
        return this.dragEnabled && this.isMobile;
      }

      disconnectedCallback() {
        document.removeEventListener('gallery:open-zoom', this.openHandler);
        document.removeEventListener('keydown', this.keydownHandler);
        this.setBackgroundInert(false);
        this.destroySliders();
      }

      open({ activeId, zoomElements, triggerEl }) {
        this.triggerEl = triggerEl || null;
        this.buildSlides(zoomElements);
        this.initSliders();
        this.classList.remove('hidden');
        this.lockScroll();
        this.setBackgroundInert(true);
        document.addEventListener('keydown', this.keydownHandler);

        const index = Array.from(zoomElements).findIndex((el) => el.dataset.id === activeId);
        if (index >= 0) {
          this.thumbsSwiper.slideTo(index, 0);
          this.zoomSwiper.slideTo(index, 0);
        }
        // Runs inside the opening tap, so mobile browsers allow playback with sound
        this.syncVideos();

        if (this.closeButton) this.closeButton.focus();
      }

      getFocusableElements() {
        return Array.from(this.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((el) => el.offsetParent !== null);
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = this.getFocusableElements();
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      setBackgroundInert(makeInert) {
        if (makeInert) {
          this.inertedElements = [];
          Array.from(document.body.children).forEach((child) => {
            if (child === this || child.contains(this) || child.hasAttribute('inert')) return;
            child.setAttribute('inert', '');
            this.inertedElements.push(child);
          });
        } else {
          this.inertedElements.forEach((child) => child.removeAttribute('inert'));
          this.inertedElements = [];
        }
      }

      buildSlides(zoomElements) {
        this.slidesWrapper.innerHTML = '';
        this.thumbsWrapper.innerHTML = '';
        this.zoomResetFns = [];

        zoomElements.forEach((el, index) => {
          const isVideo = el.hasAttribute('data-video-el');
          const slide = document.createElement('div');
          slide.className = 'swiper-slide zoom-slide';

          if (isVideo) {
            const video = el.querySelector('video')?.cloneNode(true);
            if (video) {
              video.removeAttribute('autoplay');
              video.removeAttribute('muted');
              video.removeAttribute('data-tap-to-play');
              video.setAttribute('loop', '');
              video.setAttribute('playsinline', '');
              video.playsInline = true;
              video.muted = false;
              slide.appendChild(video);
            }
          } else {
            const src = window.innerWidth < 990 ? el.dataset.zoomSrcMobile : el.dataset.zoomSrcDesktop;
            if (this.useSwiperZoom) {
              const zoomContainer = document.createElement('div');
              zoomContainer.className = 'swiper-zoom-container';
              if (src) zoomContainer.innerHTML = window.atob(src);
              slide.appendChild(zoomContainer);
            } else {
              if (src) slide.innerHTML = window.atob(src);
              this.initImageZoom(slide);
            }
          }

          this.slidesWrapper.appendChild(slide);

          const thumb = document.createElement('button');
          thumb.type = 'button';
          thumb.className = 'swiper-slide zoom-thumb';
          const labelTemplate = this.dataset.goToSlideLabel || 'Go to slide __SLIDE__';
          thumb.setAttribute('aria-label', labelTemplate.replace('__SLIDE__', index + 1));
          const thumbImage = el.querySelector('img')?.cloneNode(true);
          if (thumbImage) {
            thumbImage.removeAttribute('sizes');
            thumb.appendChild(thumbImage);
          }
          thumb.addEventListener('click', () => this.zoomSwiper && this.zoomSwiper.slideTo(index));
          this.thumbsWrapper.appendChild(thumb);
        });
      }

      initSliders() {
        if (typeof Swiper === 'undefined') return;
        this.destroySliders();

        this.thumbsSwiper = new Swiper(this.thumbsSlider, {
          slidesPerView: 'auto',
          spaceBetween: 4,
          watchSlidesProgress: true,
        });

        this.zoomSwiper = new Swiper(this.slider, {
          spaceBetween: 0,
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          mousewheel: {
            forceToAxis: true,
            thresholdDelta: 5,
            thresholdTime: 400,
          },
          zoom: this.useSwiperZoom
            ? {
                maxRatio: 2.5,
                minRatio: 1,
              }
            : false,
          navigation: {
            nextEl: this.querySelector('[data-zoom-next]'),
            prevEl: this.querySelector('[data-zoom-prev]'),
          },
          thumbs: {
            swiper: this.thumbsSwiper,
          },
        });

        this.zoomSwiper.on('slideChange', () => this.resetZoomStates());
        this.zoomSwiper.on('slideChange', () => this.updateActiveThumb());
        this.zoomSwiper.on('slideChange', () => this.syncVideos());
        this.updateActiveThumb();
      }

      // Play only the active slide's video; fall back to muted if sound is blocked
      syncVideos() {
        const activeSlide = this.zoomSwiper ? this.zoomSwiper.slides[this.zoomSwiper.activeIndex] : this.slidesWrapper.firstElementChild;
        this.slidesWrapper.querySelectorAll('video').forEach((video) => {
          if (!activeSlide || !activeSlide.contains(video)) {
            video.pause();
            return;
          }
          const promise = video.play();
          if (!promise) return;
          promise.catch(() => {
            video.muted = true;
            video.play().catch(() => {});
          });
        });
      }

      updateActiveThumb() {
        const thumbs = this.thumbsWrapper.querySelectorAll('.zoom-thumb');
        thumbs.forEach((thumb, index) => {
          if (index === this.zoomSwiper.realIndex) {
            thumb.setAttribute('aria-current', 'true');
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
      }

      resetZoomStates() {
        (this.zoomResetFns || []).forEach((reset) => reset());

        const hasZoomedSlide = this.slidesWrapper?.querySelector('.swiper-slide-zoomed');
        if (this.zoomSwiper?.zoom?.enabled && hasZoomedSlide) this.zoomSwiper.zoom.out();
      }

      destroySliders() {
        if (this.zoomSwiper) this.zoomSwiper.destroy(true, true);
        if (this.thumbsSwiper) this.thumbsSwiper.destroy(true, true);
        this.zoomSwiper = null;
        this.thumbsSwiper = null;
      }

      initImageZoom(slide) {
        const image = slide.querySelector('img');
        if (!image) return;

        image.draggable = false;

        if (!this.dragEnabled) {
          slide.addEventListener('click', () => {
            slide.classList.toggle('zoom-active');
          });
          this.zoomResetFns.push(() => slide.classList.remove('zoom-active'));
          return;
        }

        let dragging = false;
        let moved = false;
        let startX = 0;
        let startY = 0;
        let originX = 0;
        let originY = 0;
        let posX = 0;
        let posY = 0;

        const getBounds = () => {
          const slideRect = slide.getBoundingClientRect();
          return {
            maxX: Math.max(0, (image.offsetWidth - slideRect.width) / 2),
            maxY: Math.max(0, (image.offsetHeight - slideRect.height) / 2),
          };
        };

        const setPosition = (x, y) => {
          const { maxX, maxY } = getBounds();
          posX = Math.min(Math.max(x, -maxX), maxX);
          posY = Math.min(Math.max(y, -maxY), maxY);
          image.style.translate = `${posX}px ${posY}px`;
        };

        image.addEventListener('pointerdown', (event) => {
          if (!slide.classList.contains('zoom-active')) return;
          event.preventDefault();
          dragging = true;
          moved = false;
          startX = event.clientX;
          startY = event.clientY;
          originX = posX;
          originY = posY;
          image.setPointerCapture(event.pointerId);
        });

        image.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          event.preventDefault();
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved = true;
          setPosition(originX + dx, originY + dy);
        });

        ['pointerup', 'pointercancel'].forEach((type) => {
          image.addEventListener(type, () => {
            dragging = false;
          });
        });

        const zoomOff = () => {
          slide.classList.remove('zoom-active');
          image.removeAttribute('style');
          image.classList.remove('swiper-no-swiping');
          posX = 0;
          posY = 0;
        };

        slide.addEventListener('click', () => {
          if (moved) return;
          if (slide.classList.contains('zoom-active')) {
            zoomOff();
          } else {
            slide.classList.add('zoom-active');
            image.style.cssText = `width: ${image.getAttribute('width')}px; height: ${image.getAttribute('height')}px;`;
            image.classList.add('swiper-no-swiping');
          }
        });

        this.zoomResetFns.push(zoomOff);
      }

      close() {
        this.classList.add('hidden');
        this.unlockScroll();
        this.setBackgroundInert(false);
        document.removeEventListener('keydown', this.keydownHandler);
        this.destroySliders();
        this.slidesWrapper.innerHTML = '';
        this.thumbsWrapper.innerHTML = '';

        if (this.triggerEl) this.triggerEl.focus();
        this.triggerEl = null;
      }

      lockScroll() {
        document.body.style.overflow = 'hidden';
      }

      unlockScroll() {
        document.body.style.overflow = '';
      }
    },
  );
}
